import { useImmerReducer } from 'use-immer';
import type { ExternalProduct, ExternalProductOffer } from '../../../../Catalog.types';
import type { ExternalProductPdpModalForm } from '../ExternalProductOfferPdpModal';

export type SelectedExternalProductOffer = {
  itemPosition: number;
  offer: ExternalProductOffer;
};

export type ExternalProductState = {
  selectedOffer?: SelectedExternalProductOffer;
  isProductLoading: boolean;
  productLoadingError?: unknown;
  loadedProduct?: ExternalProduct;
  addProductForm?: Partial<ExternalProductPdpModalForm>;
  isProductRequired?: boolean;
  isOfferReadyToAdd: boolean;
  addOfferWhenReady?: boolean;
};

const initialState: ExternalProductState = {
  isOfferReadyToAdd: false,
  isProductLoading: false,
} as const;

export const ACTIONS = {
  SET_SELECTED_EXTERNAL_OFFER: 'SET_SELECTED_EXTERNAL_OFFER',
  SET_EXTERNAL_PRODUCT_LOADING: 'SET_EXTERNAL_PRODUCT_LOADING',
  SET_EXTERNAL_PRODUCT_LOADING_ERROR: 'SET_EXTERNAL_PRODUCT_LOADING_ERROR',
  SET_EXTERNAL_PRODUCT_LOADED: 'SET_EXTERNAL_PRODUCT_LOADED',
  ADD_EXTERNAL_OFFER_WHEN_READY: 'ADD_EXTERNAL_OFFER_WHEN_READY',
  FINISH_ADDING_EXTERNAL_OFFER: 'FINISH_ADDING_EXTERNAL_OFFER',
} as const;

type ExternalProductActions =
  | ReducerActionWithPayload<typeof ACTIONS.SET_SELECTED_EXTERNAL_OFFER, { selectedOffer: SelectedExternalProductOffer }>
  | ReducerAction<typeof ACTIONS.SET_EXTERNAL_PRODUCT_LOADING>
  | ReducerActionWithPayload<typeof ACTIONS.SET_EXTERNAL_PRODUCT_LOADING_ERROR, { productLoadingError: unknown }>
  | ReducerActionWithPayload<typeof ACTIONS.SET_EXTERNAL_PRODUCT_LOADED, { loadedProduct: ExternalProduct }>
  | ReducerActionWithPayload<typeof ACTIONS.ADD_EXTERNAL_OFFER_WHEN_READY, { addProductForm: Partial<ExternalProductPdpModalForm> }>
  | ReducerAction<typeof ACTIONS.FINISH_ADDING_EXTERNAL_OFFER>;

export const useExternalProductReducer = () => {
  return useImmerReducer<ExternalProductState, ExternalProductActions>((draft, action) => {
    switch (action.type) {
      case ACTIONS.SET_SELECTED_EXTERNAL_OFFER:
        draft = { ...initialState, selectedOffer: action.payload.selectedOffer };
        break;
      case ACTIONS.SET_EXTERNAL_PRODUCT_LOADING:
        draft.isProductLoading = true;
        break;
      case ACTIONS.SET_EXTERNAL_PRODUCT_LOADING_ERROR:
        draft.isProductLoading = false;
        draft.productLoadingError = action.payload.productLoadingError;
        break;
      case ACTIONS.SET_EXTERNAL_PRODUCT_LOADED:
        draft.isProductLoading = false;
        draft.loadedProduct = action.payload.loadedProduct;
        break;
      case ACTIONS.ADD_EXTERNAL_OFFER_WHEN_READY:
        draft.addOfferWhenReady = true;
        draft.addProductForm = action.payload.addProductForm;
        break;
      case ACTIONS.FINISH_ADDING_EXTERNAL_OFFER:
        draft.addOfferWhenReady = false;
        break;
    }
    draft.isProductRequired = !draft.selectedOffer?.offer.sellerUrl;
    draft.isOfferReadyToAdd = Boolean(draft.selectedOffer?.offer.sellerUrl || draft.loadedProduct);
    return draft;
  }, initialState);
};
